import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.prompts')

export const getPromptById = async (supabase: SupabaseClient<Database>, promptId: string) => {
  const { data: prompt, error } = await supabase
    .from("prompts")
    .select("*")
    .eq("id", promptId)
    .single()

  if (!prompt) {
    logError(logger, error, 'Failed to fetch Prompt by ID', { promptId })
    throw new Error(error?.message)
  }

  return prompt
}

export const getOptionalPromptById = async (supabase: SupabaseClient<Database>, promptId: string | null) => {
	if (!promptId) {
		return null
	}

  const { data: prompt, error } = await supabase
    .from("prompts")
    .select("*")
    .eq("id", promptId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch Prompt by ID', { promptId })
    throw new Error(error.message)
  }

  return prompt
}

export const getOptionalPromptByName = async (supabase: SupabaseClient<Database>, promptName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      prompts!prompt_workspaces (*)
    `
    )
    .eq("id", workspaceId)
    .eq("prompts.name", promptName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Prompt by name', { promptName, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.prompts[0] : null
}

export const getPromptWorkspacesByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      prompts!prompt_workspaces (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
    logError(logger, error, 'Failed to fetch Prompt workspaces by workspace ID', { workspaceId })
    throw new Error(error.message)
  }

  return workspace.prompts ?? []
}

export const getPromptsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'prompts'>[]> => {
	const wsPrompts = await getPromptWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'prompts'>[] = []

	for (const [i, prompt] of wsPrompts.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "prompts", context: "ANY", uuid: prompt.id, tenant:prompt.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "prompts", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...prompt, permissions: perms })
	}

	return result
}

export const getPromptWorkspacesByPromptId = async (supabase: SupabaseClient<Database>, promptId: string) => {
  const { data: prompt, error } = await supabase
    .from("prompts")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", promptId)
    .single()

  if (!prompt) {
    logError(logger, error, 'Failed to fetch Prompt workspaces by Prompt ID', { promptId })
    throw new Error(error.message)
  }

  return prompt
}

export const getAutoSharePrompts = async (supabase: SupabaseClient<Database>) => {
  const { data: prompts, error } = await supabase
    .from("prompts")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-share Prompts')
    throw new Error(error.message)
  }

  return prompts
}

export const createPrompt = async (
	supabase: SupabaseClient<Database>,
  prompt: TablesInsert<"prompts">,
	workspace: Tables<"workspaces">
) => {
	const promptWithTenant = {
		...prompt,
		owning_tenant: workspace.tenant_id
	}

  const { data: createdPrompt, error } = await supabase
    .from("prompts")
    .insert([promptWithTenant])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Prompt', { prompt: printObject(prompt) })
    throw new Error(error.message)
  }

  await createPromptWorkspace(supabase, {
    user_id: createdPrompt.user_id,
    prompt_id: createdPrompt.id,
    workspace_id: workspace.id
  })

  logger.info('Prompt created successfully', { promptId: createdPrompt.id })
  return createdPrompt
}

export const createPrompts = async (
	supabase: SupabaseClient<Database>,
  prompts: TablesInsert<"prompts">[],
	workspace: Tables<"workspaces">
) => {
	const promptsWithTenant = prompts.map(prompt => ({
		...prompt,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdPrompts, error } = await supabase
    .from("prompts")
    .insert(promptsWithTenant)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Prompts', { prompts: printObject(prompts) })
    throw new Error(error.message)
  }

  await createPromptWorkspaces(
		supabase,
    createdPrompts.map(prompt => ({
      user_id: prompt.user_id,
      prompt_id: prompt.id,
	    workspace_id: workspace.id
    }))
  )

  logger.info('Prompts created successfully', { prompts: printObject(createdPrompts) })
  return createdPrompts
}

export const createPromptWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  prompt_id: string
  workspace_id: string
}) => {
  const { data: createdPromptWorkspace, error } = await supabase
    .from("prompt_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Prompt workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info('Prompt workspace created successfully', { promptWorkspace: printObject(createdPromptWorkspace) })
  return createdPromptWorkspace
}

export const createPromptWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; prompt_id: string; workspace_id: string }[]
) => {
  const { data: createdPromptWorkspaces, error } = await supabase
    .from("prompt_workspaces")
    .upsert(items, { onConflict: 'prompt_id, workspace_id' })
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Prompt workspaces', { items: printObject(items) })
    throw new Error(error.message)
  }

  return createdPromptWorkspaces
}

export const updatePrompt = async (
	supabase: SupabaseClient<Database>,
  promptId: string,
  prompt: TablesUpdate<"prompts">
) => {
  const { data: updatedPrompt, error } = await supabase
    .from("prompts")
    .update(prompt)
    .eq("id", promptId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to update Prompt', { promptId, prompt: printObject(prompt) })
    throw new Error(error.message)
  }

  return updatedPrompt
}

export const deletePrompt = async (supabase: SupabaseClient<Database>, promptId: string) => {
  const { error } = await supabase.from("prompts").delete().eq("id", promptId)

  if (error) {
    logError(logger, error, 'Failed to delete Prompt', { promptId })
    throw new Error(error.message)
  }

  return true
}

export const deletePromptWorkspace = async (
	supabase: SupabaseClient<Database>,
  promptId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("prompt_workspaces")
    .delete()
    .eq("prompt_id", promptId)
    .eq("workspace_id", workspaceId)

  if (error) {
    logError(logger, error, 'Failed to delete Prompt workspace', { promptId, workspaceId })
    throw new Error(error.message)
  }

  return true
}
